import React, { useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  LinearProgress,
  Avatar,
} from '@mui/material';
import { Add, EmojiEvents, Palette } from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { jerseyApi } from '../api/jerseyApi';

const Dashboard: React.FC = () => {
  const navigate = useNavigate();

  // Auto-open Jersey editor when `?colours=` is present on Dashboard
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const colours = params.get('colours');
    if (colours) {
      navigate(`/jerseys?action=create&prefill=${encodeURIComponent(colours)}`);
    }
  }, [navigate]);

  // Fetch dashboard statistics
  const { data: stats, isLoading: statsLoading } = useQuery({
    queryKey: ['dashboard-stats'],
    queryFn: () => jerseyApi.getDashboardStats(),
    refetchInterval: 30000, // Refresh every 30 seconds
  });

  // Fetch recent jerseys (removed unused variables)
  useQuery({
    queryKey: ['recent-jerseys'],
    queryFn: () => jerseyApi.getJerseys({ limit: 5 }),
  });




  const StatCard: React.FC<{
    title: string;
    value: number;
    subtitle?: string;
    icon: React.ReactNode;
    color: 'primary' | 'secondary' | 'success' | 'warning' | 'error';
    progress?: number;
  }> = ({ title, value, subtitle, icon, color, progress }) => (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Avatar sx={{ bgcolor: `${color}.main`, mr: 2 }}>
            {icon}
          </Avatar>
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="h4" component="div">
              {value.toLocaleString()}
            </Typography>
            <Typography variant="h6" color="text.secondary">
              {title}
            </Typography>
          </Box>
        </Box>
        {subtitle && (
          <Typography variant="body2" color="text.secondary">
            {subtitle}
          </Typography>
        )}
        {progress !== undefined && (
          <Box sx={{ mt: 2 }}>
            <LinearProgress
              variant="determinate"
              value={progress}
              color={color}
              sx={{ height: 8, borderRadius: 4 }}
            />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {progress.toFixed(1)}% completion rate
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );

  const QuickActionCard: React.FC<{
    title: string;
    description: string;
    icon: React.ReactNode;
    onClick: () => void;
    color: string;
  }> = ({ title, description, icon, onClick, color }) => (
    <Card sx={{ cursor: 'pointer', '&:hover': { elevation: 4 } }} onClick={onClick}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Avatar sx={{ bgcolor: color, mr: 2 }}>
            {icon}
          </Avatar>
          <Box>
            <Typography variant="h6">{title}</Typography>
            <Typography variant="body2" color="text.secondary">
              {description}
            </Typography>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );

  if (statsLoading) {
    return (
      <Box sx={{ p: 3 }}>
        <LinearProgress />
        <Typography sx={{ mt: 2 }}>Loading dashboard...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" gutterBottom>
          Dashboard
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Welcome to the Jersey Colours Management System
        </Typography>
      </Box>




      {/* Quick Actions */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <QuickActionCard
            title="Create Jersey"
            description="Add a new jersey design"
            icon={<Add />}
            onClick={() => navigate('/jerseys?action=create')}
            color="primary.main"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <QuickActionCard
            title="Pattern Library"
            description="Browse and upload patterns"
            icon={<Palette />}
            onClick={() => navigate('/patterns')}
            color="success.main"
          />
        </Grid>
      </Grid>

      {/* Create New Jersey and Preview */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Create New Jersey & Preview
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Pass a colour string via the URL like /dashboard?colours=blue, white stars to prefill the editor.
              </Typography>
              <Button variant="contained" startIcon={<Add />} onClick={() => {
                const params = new URLSearchParams(window.location.search);
                const colours = params.get('colours') || '';
                const url = colours ? `/jerseys?action=create&prefill=${encodeURIComponent(colours)}` : '/jerseys?action=create';
                navigate(url);
              }}>
                Open Jersey Editor
              </Button>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Stats summary */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Patterns"
            value={stats?.patterns?.total || 0}
            icon={<Palette />}
            color="success"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Colours"
            value={stats?.colours?.total || 0}
            icon={<Palette />}
            color="secondary"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Approved Silks"
            value={stats?.approved_silks?.total || 0}
            icon={<EmojiEvents />}
            color="primary"
          />
        </Grid>
      </Grid>

    </Box>
  );
};

export default Dashboard;
